import { FC, useEffect, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ShoppingBagIcon,
} from '@heroicons/react/outline';
import { useGiftAuthGate } from '@service/giftStore/components/GiftStoreAuthGate';
import {
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import {
  GiftDeliveryForStore,
  GiftDeliveryShareStatus,
  GiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import {
  giftOrderListForStoreDataAtomFamily,
  giftOrderListForStoreStatusAtomFamily,
} from '@shared/api/giftOrder/giftOrder.atom';
import { useGiftOrderListForStoreHook } from '@shared/api/giftOrder/giftOrder.hook';
import {
  GiftOrderDeliveryMode,
  GiftOrderForStore,
  GiftOrderItemType,
  GiftOrderPaymentStatus,
} from '@shared/api/giftOrder/giftOrder.interface';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { formatDate } from '@shared/utils/formatDate';
import { formatPrice } from '@shared/utils/formatPrice';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { giftStoreNavBarTitleAtom } from '../containers/GiftStoreNavBar/giftStoreNavBar.atom';

// 주문 타입 분석
const getOrderTypeInfo = (order: GiftOrderForStore) => {
  const couponItems = order.orderDetails.filter(
    (item) => item.type === GiftOrderItemType.Coupon,
  );
  const deliveryItems = order.orderDetails.filter(
    (item) => item.type === GiftOrderItemType.Delivery,
  );
  const hasCoupon = couponItems.length > 0;
  const hasDelivery = deliveryItems.length > 0;

  const activeCoupons = (
    (order.coupons ?? []) as { redeemStatus: GiftCouponRedeemStatus }[]
  ).filter((c) => c.redeemStatus !== GiftCouponRedeemStatus.Canceled);
  const activeDeliveries = ((order.deliveries ?? []) as GiftDeliveryForStore[]).filter(
    (d) => d.deliveryStatus !== GiftDeliveryStatus.Canceled,
  );

  const couponCount =
    activeCoupons.length > 0
      ? activeCoupons.length
      : couponItems.reduce((sum, item) => sum + item.quantity, 0);
  const deliveryCount =
    activeDeliveries.length > 0
      ? activeDeliveries.length
      : deliveryItems.reduce((sum, item) => sum + item.quantity, 0);

  const isBulk = order.deliveryMode === GiftOrderDeliveryMode.Bulk;
  const isIndividual = order.deliveryMode === GiftOrderDeliveryMode.Individual;

  return { hasCoupon, hasDelivery, couponCount, deliveryCount, isBulk, isIndividual };
};

// Bulk 배송 대표 상태 (deliveries virtual에서 가져옴)
const getBulkDeliveryStatus = (order: GiftOrderForStore): GiftDeliveryStatus | null => {
  const deliveries = (order.deliveries ?? []) as GiftDeliveryForStore[];
  if (deliveries.length === 0) return null;
  return deliveries[0]?.deliveryStatus ?? null;
};

// 소비자용 배송 상태 라벨 (AddressSubmitted = Preparing = "상품 준비중")
const getConsumerDeliveryLabel = (status: GiftDeliveryStatus): string => {
  switch (status) {
    case GiftDeliveryStatus.WaitingAddress:
      return '배송지 입력 대기';
    case GiftDeliveryStatus.AddressSubmitted:
    case GiftDeliveryStatus.Preparing:
      return '상품 준비중';
    case GiftDeliveryStatus.Shipping:
      return '배송중';
    case GiftDeliveryStatus.Delivered:
      return '배송 완료';
    case GiftDeliveryStatus.Canceled:
      return '취소';
    default:
      return status;
  }
};

const getDeliveryStatusColor = (status: GiftDeliveryStatus) => {
  switch (status) {
    case GiftDeliveryStatus.AddressSubmitted:
    case GiftDeliveryStatus.Preparing:
      return 'bg-indigo-100 text-indigo-700';
    case GiftDeliveryStatus.Shipping:
      return 'bg-purple-100 text-purple-700';
    case GiftDeliveryStatus.Delivered:
      return 'bg-green-100 text-green-700';
    case GiftDeliveryStatus.Canceled:
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-blue-100 text-blue-700';
  }
};

// 전달 진행률 계산
const getShareProgress = (order: GiftOrderForStore) => {
  const allCoupons = (order.coupons ?? []) as {
    shareStatus: GiftCouponShareStatus;
    redeemStatus: GiftCouponRedeemStatus;
  }[];
  const allDeliveries = (order.deliveries ?? []) as {
    shareStatus: GiftDeliveryShareStatus;
    deliveryStatus: GiftDeliveryStatus;
  }[];
  const { isIndividual } = getOrderTypeInfo(order);

  const coupons = allCoupons.filter(
    (c) => c.redeemStatus !== GiftCouponRedeemStatus.Canceled,
  );
  const deliveries = allDeliveries.filter(
    (d) => d.deliveryStatus !== GiftDeliveryStatus.Canceled,
  );

  const shareableDeliveries = isIndividual ? deliveries : [];
  const total = coupons.length + shareableDeliveries.length;
  if (total === 0) return null;

  const shared =
    coupons.filter((c) => c.shareStatus !== GiftCouponShareStatus.NotShared).length +
    shareableDeliveries.filter((d) => d.shareStatus !== GiftDeliveryShareStatus.NotShared)
      .length;

  return { total, shared, allDone: shared === total };
};

// 혼합 주문 상세보기 - 타입별 그룹 섹션
const OrderDetailSection: FC<{
  order: GiftOrderForStore;
  bugoId: string | null;
  bulkStatus: GiftDeliveryStatus | null;
}> = ({ order, bugoId, bulkStatus }) => {
  const basePath = `/gift-store/order/${order._id}`;
  const qs = `bugoId=${bugoId}`;
  const isBulk = order.deliveryMode === GiftOrderDeliveryMode.Bulk;

  const couponItems = order.orderDetails.filter(
    (item) => item.type === GiftOrderItemType.Coupon,
  );
  const deliveryItems = order.orderDetails.filter(
    (item) => item.type === GiftOrderItemType.Delivery,
  );

  return (
    <div className="flex flex-col gap-2 border-t border-gray-100 px-4 pb-3 pt-2.5">
      {/* 쿠폰형 그룹 */}
      {couponItems.length > 0 && (
        <Link
          to={`${basePath}/coupon?${qs}`}
          onClick={(e) => e.stopPropagation()}
          className="flex items-center justify-between rounded-lg bg-purple-50 px-3 py-2"
        >
          <div className="flex items-center gap-2">
            <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-bold text-purple-700">
              쿠폰형
            </span>
            <span className="text-sm text-gray-700">
              {couponItems.length === 1
                ? couponItems[0]?.giftItemEmbed?.name
                : `${couponItems[0]?.giftItemEmbed?.name} 외 ${couponItems.length - 1}건`}
            </span>
          </div>
          <span className="flex items-center gap-0.5 text-xs font-medium text-purple-600">
            쿠폰 전달하기
            <ChevronRightIcon className="h-3.5 w-3.5" />
          </span>
        </Link>
      )}

      {/* 배송형 그룹 */}
      {deliveryItems.length > 0 && (
        <Link
          to={`${basePath}/${isBulk ? 'delivery-status' : 'delivery'}?${qs}`}
          onClick={(e) => e.stopPropagation()}
          className={`flex flex-col gap-1.5 rounded-lg px-3 py-2 ${
            isBulk ? 'bg-blue-50' : 'bg-purple-50'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span
                className={`flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold ${
                  isBulk ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                }`}
              >
                {isBulk ? '배송형(일괄)' : '배송형(개별)'}
              </span>
              <span className="text-sm text-gray-700">
                {deliveryItems.length === 1
                  ? deliveryItems[0]?.giftItemEmbed?.name
                  : `${deliveryItems[0]?.giftItemEmbed?.name} 외 ${
                      deliveryItems.length - 1
                    }건`}
              </span>
            </div>
            <span
              className={`flex flex-shrink-0 items-center gap-0.5 text-xs font-medium ${
                isBulk ? 'text-blue-600' : 'text-purple-600'
              }`}
            >
              {isBulk ? '배송현황 보기' : '답례품 전달하기'}
              <ChevronRightIcon className="h-3.5 w-3.5" />
            </span>
          </div>
          {isBulk && bulkStatus && (
            <div className="flex items-center">
              <span
                className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${getDeliveryStatusColor(
                  bulkStatus,
                )}`}
              >
                {getConsumerDeliveryLabel(bulkStatus)}
              </span>
            </div>
          )}
        </Link>
      )}
    </div>
  );
};

// 주문 카드 컴포넌트
const OrderCard: FC<{ order: GiftOrderForStore; bugoId: string | null }> = ({
  order,
  bugoId,
}) => {
  const navigate = useNavigate();
  const [showDetail, setShowDetail] = useState(false);

  const firstItem = order.orderDetails[0];
  const totalItems = order.orderDetails.length;

  const { hasCoupon, hasDelivery, couponCount, deliveryCount, isBulk, isIndividual } =
    getOrderTypeInfo(order);

  const isMixed = hasCoupon && hasDelivery;

  const productName =
    totalItems > 1
      ? `${firstItem?.giftItemEmbed?.name || '상품'} 외 ${totalItems - 1}건`
      : firstItem?.giftItemEmbed?.name || '상품';

  const imageUrl =
    typeof firstItem?.giftItem === 'object' &&
    firstItem?.giftItem?.giftItemInfo?.image?.url
      ? firstItem.giftItem.giftItemInfo.image.url
      : typeof firstItem?.giftItem === 'object' && firstItem?.giftItem?.image?.url
      ? firstItem.giftItem.image.url
      : '';

  const isPaid =
    order.paymentDetail.status === GiftOrderPaymentStatus.Paid ||
    order.paymentDetail.status === GiftOrderPaymentStatus.Confirmed;
  const isCanceled =
    order.paymentDetail.status === GiftOrderPaymentStatus.Canceled;
  const isCancelRequested =
    order.paymentDetail.status === GiftOrderPaymentStatus.CancelRequested;

  const bulkStatus = isBulk ? getBulkDeliveryStatus(order) : null;
  const shareProgress = isPaid ? getShareProgress(order) : null;

  const basePath = `/gift-store/order/${order._id}`;
  const qs = `bugoId=${bugoId}`;

  const getCTAs = () => {
    if (!isPaid) return [];

    // 혼합: 전달하기 하나로 묶어서 coupon 페이지로
    if (isMixed) {
      return [
        {
          label: '답례품 전달하기',
          to: `${basePath}/coupon?${qs}`,
          color: 'text-purple-600',
        },
      ];
    }

    if (hasCoupon) {
      return [
        {
          label: '쿠폰 전달하기',
          to: `${basePath}/coupon?${qs}`,
          color: 'text-purple-600',
        },
      ];
    }
    if (hasDelivery && isIndividual) {
      return [
        {
          label: '답례품 전달하기',
          to: `${basePath}/delivery?${qs}`,
          color: 'text-purple-600',
        },
      ];
    }
    if (hasDelivery && isBulk) {
      return [
        {
          label: '배송현황 보기',
          to: `${basePath}/delivery-status?${qs}`,
          color: 'text-blue-600',
        },
      ];
    }
    return [];
  };

  const ctas = getCTAs();

  const defaultLink = hasCoupon
    ? `${basePath}/coupon?${qs}`
    : isBulk
    ? `${basePath}/delivery-status?${qs}`
    : `${basePath}/delivery?${qs}`;

  return (
    <div
      className={`rounded-lg bg-white shadow transition hover:shadow-md ${
        isCanceled ? 'opacity-60' : 'cursor-pointer'
      }`}
    >
      <div className="block p-4" onClick={() => !isCanceled && navigate(defaultLink)}>
        <div className="flex gap-3">
          {/* 이미지 */}
          <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded bg-gray-100">
            {imageUrl ? (
              <img
                src={fileBucketUrl(imageUrl, 'w200')}
                alt={productName}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                No Image
              </div>
            )}
            {hasCoupon && (
              <div className="absolute bottom-0 left-0 right-0 bg-purple-600/90 py-0.5 text-center text-[10px] font-bold text-white">
                쿠폰 {couponCount}장
              </div>
            )}
          </div>

          {/* 주문 정보 */}
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <p className="break-keep font-medium">{productName}</p>
                <p className="mt-0.5 text-sm text-gray-500">
                  {hasCoupon && `전달형 ${couponCount}개`}
                  {hasCoupon && hasDelivery && ', '}
                  {hasDelivery && `직접수령 ${deliveryCount}개`}
                </p>
              </div>
              {(isCanceled || isCancelRequested) ? (
                <span
                  className={`flex-shrink-0 break-keep rounded-full px-2 py-0.5 text-[10px] font-bold ${
                    isCanceled
                      ? 'bg-red-100 text-red-700'
                      : 'bg-orange-100 text-orange-700'
                  }`}
                >
                  {isCanceled ? '취소됨' : '취소요청'}
                </span>
              ) : isPaid && (shareProgress || (isBulk && bulkStatus)) ? (
                <div className="flex flex-shrink-0 flex-col items-end gap-1">
                  {shareProgress && (
                    <span
                      className={`break-keep rounded-full px-2 py-0.5 text-[10px] font-bold ${
                        shareProgress.allDone
                          ? 'bg-green-100 text-green-700'
                          : 'bg-purple-100 text-purple-700'
                      }`}
                    >
                      {shareProgress.allDone
                        ? '모두 전달완료'
                        : `전달 ${shareProgress.shared}/${shareProgress.total}`}
                    </span>
                  )}
                  {isBulk && bulkStatus && (
                    <span
                      className={`break-keep rounded-full px-2 py-0.5 text-[10px] font-bold ${getDeliveryStatusColor(
                        bulkStatus,
                      )}`}
                    >
                      {getConsumerDeliveryLabel(bulkStatus)}
                    </span>
                  )}
                </div>
              ) : null}
            </div>

            <div className="mt-2 flex items-center justify-between">
              <p className="text-sm text-gray-400">
                {formatDate(order.createdAt, { dateSeparater: '.' })}
              </p>
              <div className="text-right">
                <p
                  className={`font-bold ${
                    isCanceled ? 'text-gray-400 line-through' : 'text-red-500'
                  }`}
                >
                  {formatPrice(order.paymentDetail.paymentByCash)}원
                </p>
                {!isCanceled &&
                  !isCancelRequested &&
                  (order.paymentDetail.cancelledTotalPrice ?? 0) > 0 && (
                    <p className="text-xs text-gray-400">
                      <span className="text-red-400">
                        -{formatPrice(order.paymentDetail.cancelledTotalPrice ?? 0)}원
                      </span>{' '}
                      부분취소
                    </p>
                  )}
              </div>
            </div>

            {/* CTA (단일 타입일 때만) */}
            {ctas.length > 0 && (
              <div className="mt-2 flex flex-col items-end gap-1">
                {ctas.map((cta, idx) => (
                  <Link
                    key={idx}
                    to={cta.to}
                    onClick={(e) => e.stopPropagation()}
                    className={`flex items-center gap-1 text-sm font-medium ${cta.color}`}
                  >
                    <span>{cta.label}</span>
                    <ChevronRightIcon className="h-4 w-4" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 혼합 주문: 상세보기 토글 */}
      {isMixed && isPaid && (
        <>
          <button
            onClick={(e) => {
              e.preventDefault();
              setShowDetail((prev) => !prev);
            }}
            className="flex w-full items-center justify-center gap-1 border-t border-gray-100 py-2.5 text-sm font-medium text-gray-500 active:bg-gray-50"
          >
            <span>주문한 상품 상세보기</span>
            {showDetail ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
          </button>

          {showDetail && (
            <OrderDetailSection order={order} bugoId={bugoId} bulkStatus={bulkStatus} />
          )}
        </>
      )}
    </div>
  );
};

const GiftOrderListPage: FC = function GiftOrderListPage() {
  const [searchParams] = useSearchParams();
  const bugoId = searchParams.get('bugoId');

  const setTitle = useSetRecoilState(giftStoreNavBarTitleAtom);
  const { storeRole } = useAuth();
  const { requestReauth } = useGiftAuthGate();

  // 주문 목록 조회
  const role = storeRole();
  useGiftOrderListForStoreHook(role);
  const status = useRecoilValue(giftOrderListForStoreStatusAtomFamily(role));
  const orderList = useRecoilValue(giftOrderListForStoreDataAtomFamily(role));

  console.log(orderList);

  useEffect(() => {
    setTitle('구매내역');
  }, [setTitle]);

  const filteredOrders = useMemo(() => {
    if (!orderList) return [];
    return orderList.filter(
      (order) =>
        order.paymentDetail.status === GiftOrderPaymentStatus.Paid ||
        order.paymentDetail.status === GiftOrderPaymentStatus.Confirmed ||
        order.paymentDetail.status === GiftOrderPaymentStatus.Waiting ||
        order.paymentDetail.status === GiftOrderPaymentStatus.CancelRequested ||
        order.paymentDetail.status === GiftOrderPaymentStatus.Canceled,
    );
  }, [orderList]);

  // 주문 목록 조회 실패(권한 없음 등) → 즉시 재로그인 폼으로 전환
  useEffect(() => {
    if (status === 'error') {
      requestReauth();
    }
  }, [status, requestReauth]);

  // 로딩 중 또는 에러(재로그인 전환 대기)
  if (status === 'loading' || status === 'idle' || status === 'error') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* 주문 목록 */}
      {filteredOrders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20">
          <ShoppingBagIcon className="h-16 w-16 text-gray-300" />
          <p className="mt-4 text-gray-500">구매내역이 없습니다</p>
          <Link
            to={`/gift-store?bugoId=${bugoId}`}
            className="mt-4 rounded-lg bg-red-500 px-6 py-2 text-white"
          >
            쇼핑하러 가기
          </Link>
        </div>
      ) : (
        <div className="space-y-3 p-4">
          {filteredOrders.map((order) => (
            <OrderCard key={order._id} order={order} bugoId={bugoId} />
          ))}
        </div>
      )}
    </div>
  );
};

export default GiftOrderListPage;
